// src/App.tsx - Updated to render the new AdminDashboard
import React, { useEffect, useCallback, useState, useRef, memo } from 'react';
import { Loader2, AlertTriangle, Search, Users, BookOpen, ChevronRight, List, Building2 } from 'lucide-react';
import { AppProvider } from './context/AppProvider';
import { useAppContext } from './hooks/useAppContext';
import { useApi } from './hooks/useApi';
import { useDebounce } from './hooks/useDebounce';
import { useFirebaseAuth } from './hooks/useFirebaseAuth';
import { useAuth } from './hooks/useAuth';
import { useBilling } from './hooks/useBilling';
import { formatPrice } from './types';
import { API_BASE_URL } from './constants';
import NotificationCenter from './components/NotificationCenter';
import ConfirmationDialog from './components/ConfirmationDialog';
import ListSelectionModal from './components/ListSelectionModal';
import ExportFieldsModal from './components/ExportFieldsModal';
import AccountListModal from './components/AccountListModal';
import CreateAccountListModal from './components/CreateAccountListModal';
import FilterSidebar from './components/FilterSidebar';
import DataTable from './components/DataTable';
import Pagination from './components/Pagination';
import TopNav from './components/TopNav';
import Terms from './components/Terms';
import PrivacyPolicy from "./components/PrivacyPolicy";
import LoginPage from "./components/LoginPage";
import AdminDashboard from "./components/AdminDashboard"; // ✅ NEW: Use the unified dashboard
import ErrorBoundary from "./components/ErrorBoundary";
import AccountPage from './components/AccountPage';
import LandingPage from './components/LandingPage';
import EnrichmentPage from './components/EnrichmentPage';
import DataDocumentationPage from './components/DataDocumentationPage';
import { DEBOUNCE_DELAY, MAINTENANCE_MODE } from "./constants";
import type { RateLimitInfo, CircuitBreakerState, UserList, AccountList, DataSourceType } from './types';
import icon from "./assets/icon.png";
import AuthProvider from './context/AuthContext';

const LoadingState = memo(() => (
  <div className="app-loading">
    <div className="loading-content">
      <div className="logo">
        <img src={icon} alt="App Icon" className="logo-icon" style={{ width: 48, height: 48 }} />
        <div className="logo-text" style={{ fontFamily: 'Avenir, Inter, -apple-system, sans-serif' }}>ppl.contact</div>
      </div>
      <Loader2 size={32} className="spinning" />
    </div>
  </div>
));
LoadingState.displayName = 'LoadingState';

const ErrorState = memo(({ error, schemaError, apiKey, rateLimitInfo, circuitBreakerState }: {
  error?: string | null; schemaError?: string | null; apiKey?: string | null;
  rateLimitInfo?: RateLimitInfo | null; circuitBreakerState?: CircuitBreakerState | null;
}) => {
  const isAuthError = schemaError?.includes('authentication') || error?.includes('authentication');
  const isRateLimitError = schemaError?.includes('Rate limit') || error?.includes('Rate limit');
  const isCircuitBreakerError = schemaError?.includes('circuit breaker') || error?.includes('circuit breaker');
  return (
    <div className="error-page">
      <div className="error-content">
        <AlertTriangle size={64} />
        <h2>
          {isAuthError ? 'API Authentication Error' : isRateLimitError ? 'Rate Limit Exceeded' :
           isCircuitBreakerError ? 'Service Protection Active' : schemaError ? 'Schema Configuration Error' : 'API Server Required'}
        </h2>
        {isAuthError ? (
          <div>
            <p>API authentication failed. Please verify:</p>
            <ul style={{ textAlign: 'left', marginTop: 16 }}>
              <li><strong>API Token:</strong> {apiKey}</li>
              <li><strong>API Server:</strong> {API_BASE_URL}</li>
              <li><strong>Permissions:</strong> Token has search and metadata access</li>
            </ul>
            <div style={{ marginTop: 16, padding: 12, background: '#f8f9fa', borderRadius: 8, fontSize: 14, color: '#666' }}>
              <strong>Error:</strong> {schemaError || error}
            </div>
          </div>
        ) : isRateLimitError ? (
          <div style={{ textAlign: 'center' }}>
            <p>API rate limit exceeded. The system will automatically retry.</p>
            {rateLimitInfo && (
              <div style={{ marginTop: 16, padding: 12, background: '#fef3c7', borderRadius: 8, fontSize: 14 }}>
                <strong>Rate Limit Status:</strong> {rateLimitInfo.remaining}/{rateLimitInfo.limit} remaining<br />
                <strong>Reset Time:</strong> {new Date(rateLimitInfo.reset).toLocaleTimeString()}
              </div>
            )}
          </div>
        ) : isCircuitBreakerError ? (
          <div style={{ textAlign: 'center' }}>
            <p>Service protection is active due to repeated failures. The system will automatically retry when the service recovers.</p>
            <div style={{ marginTop: 16, padding: 12, background: '#fef3c7', borderRadius: 8, fontSize: 14 }}>
              <strong>Protection Status:</strong> {circuitBreakerState?.state}<br />
              <strong>Failure Count:</strong> {circuitBreakerState?.failureCount}
            </div>
          </div>
        ) : (<p>{schemaError ? `Schema configuration failed: ${schemaError}` : 'Please start the API server with authentication enabled:'}</p>)}
        {!schemaError && !isAuthError && !isRateLimitError && !isCircuitBreakerError && (<pre>python main.py</pre>)}
        <div className="error-actions">
          <button onClick={() => window.location.reload()} className="button-primary">Retry Connection</button>
        </div>
      </div>
    </div>
  );
});
ErrorState.displayName = 'ErrorState';

const AppContent: React.FC = memo(() => {
  const [listPickerLists, setListPickerLists] = useState<UserList[]>([]);
  const [accountPickerLists, setAccountPickerLists] = useState<AccountList[]>([]);
  const [listPickerLoading, setListPickerLoading] = useState(false);
  const [listSelectionModalOpen, setListSelectionModalOpen] = useState(false);
  const [exportFieldsModalOpen, setExportFieldsModalOpen] = useState(false);
  const [accountListModalOpen, setAccountListModalOpen] = useState(false);
  const [createAccountListModalOpen, setCreateAccountListModalOpen] = useState(false);
  const [filterSidebarOpen, setFilterSidebarOpen] = useState(true);
  const { state, dispatch, showToast, setNavigation, setView, unlockSelectedContacts, unlockSelectedAccounts, addSelectedContactsToList, addSelectedAccountsToList, deleteContactsJobAsync, removeFromListJobAsync, removeFromAccountListJobAsync, deleteAccountsJobAsync, exportSelectedContacts, deleteList, deleteAccountList } = useAppContext();
  const { isAuthenticated } = useFirebaseAuth();
  const { apiKey, hasPermission, user: authUser } = useAuth();
  const { searchContacts, searchAccounts, checkApiHealth, getSchema, loadInitialMetadata, loadDataSourceCount, refreshMetadata, getLists, getAccountLists, requestTrackerState, rateLimitInfo, circuitBreakerState } = useApi();
  useBilling();
  const debouncedQuery = useDebounce(state.searchState.query, DEBOUNCE_DELAY);
  const selectedCount = state.selection.selectedIds.size;
  const currentDataSource = state.searchState.dataSource as DataSourceType;
  const allSelected = state.selection.selectAll;
  const initializationStarted = useRef(false);
  const isInitialLoad = useRef(true);
  const lastSearchParams = useRef<string>('');
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastNavigationState = useRef(state.navigationState);
  const countsLoaded = useRef(false);
  const backgroundCountsLoading = useRef(false);

  const handleNavigationChange = useCallback(() => {
    if (state.initialized && state.navigationState !== lastNavigationState.current) {
      const { view, listId } = state.navigationState;
      // Determine data source based on view and current context
      let dataSource = state.searchState.dataSource;
      if (view === 'people') {
        dataSource = 'people';
      } else if (view === 'company') {
        dataSource = 'company';
      } else if (view === 'list') {
        // For list view, determine based on current data source context
        // If we're on accounts view, use 'accounts', otherwise use 'contacts' for people lists
        dataSource = state.searchState.dataSource === 'accounts' ? 'accounts' : 'contacts';
      }
      
      const filters = listId ? { list_ids: { type: 'picklist', values: [listId], operator: 'OR' } } : {};
      refreshMetadata(undefined, filters, undefined, dataSource);
      lastNavigationState.current = state.navigationState;
    }
  }, [state.initialized, state.navigationState, state.searchState.dataSource, refreshMetadata]);

  useEffect(() => { handleNavigationChange(); }, [handleNavigationChange]);

  const initializeApp = useCallback(async () => {
    if (initializationStarted.current || state.initialized || state.initializing) return;
    initializationStarted.current = true;
    dispatch({ type: 'SET_INITIALIZING', payload: true });
    try {
      if (!circuitBreakerState.canRequest) throw new Error(`Service temporarily unavailable (circuit breaker ${circuitBreakerState.state})`);
      if (rateLimitInfo && rateLimitInfo.remaining === 0) {
        const waitTime = Math.max(0, rateLimitInfo.reset - Date.now());
        if (waitTime > 0) {
          showToast({ type: 'info', title: 'Rate Limit', message: `Waiting ${Math.round(waitTime / 1000)}s...`, duration: 5000 });
          await new Promise(resolve => setTimeout(resolve, waitTime));
        }
      }
      const isHealthy = await checkApiHealth();
      if (!isHealthy) throw new Error('Unable to connect to API server or authentication failed');
      dispatch({ type: 'SET_API_CONNECTED', payload: true });
      dispatch({ type: 'SET_SCHEMA_LOADING', payload: true });
      const schema = await getSchema(currentDataSource);
      if (!schema) throw new Error('Failed to load schema configuration - check API authentication');
      dispatch({ type: 'SET_SCHEMA', payload: schema, dataSource: currentDataSource });
      loadInitialMetadata().catch(error => console.warn('⚠️ Metadata loading failed, continuing with initialization:', error));
      dispatch({ type: 'SET_INITIALIZED', payload: true });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to initialize application';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
      dispatch({ type: 'SET_API_CONNECTED', payload: false });
      dispatch({ type: 'SET_INITIALIZING', payload: false });
      initializationStarted.current = false;
      if (errorMessage.includes('schema')) dispatch({ type: 'SET_SCHEMA_ERROR', payload: errorMessage });
      if (errorMessage.includes('circuit breaker')) {
        showToast({ type: 'error', title: 'Service Unavailable', message: 'API service is temporarily unavailable. Will retry automatically...', duration: 10000 });
        retryTimeoutRef.current = setTimeout(() => { initializationStarted.current = false; initializeApp(); }, 30000);
      } else if (errorMessage.includes('authentication') || errorMessage.includes('Authentication')) {
        showToast({ type: 'error', title: 'Authentication Failed', message: 'API authentication failed. Check API key.', duration: 10000 });
      } else if (errorMessage.includes('Rate limit')) {
        showToast({ type: 'warning', title: 'Rate Limited', message: 'API rate limit exceeded. Will retry automatically...', duration: 8080 });
        retryTimeoutRef.current = setTimeout(() => { initializationStarted.current = false; initializeApp(); }, 10000);
      } else {
        showToast({ type: 'error', title: 'Initialization Failed', message: errorMessage, duration: 7000 });
      }
    }
  }, [checkApiHealth, getSchema, loadInitialMetadata, dispatch, showToast, state.initialized, state.initializing, circuitBreakerState, rateLimitInfo]);

  const loadBackgroundCounts = useCallback(async () => {
    if (countsLoaded.current || !state.initialized || backgroundCountsLoading.current) return;
    try {
      backgroundCountsLoading.current = true;
      if (requestTrackerState.isSearching) await new Promise(resolve => setTimeout(resolve, 2000));
      const [peopleCount, contactsCount] = await Promise.allSettled([loadDataSourceCount('people'), loadDataSourceCount('contacts')]);
      if (peopleCount.status === 'fulfilled' && peopleCount.value !== null) dispatch({ type: 'SET_DATA_SOURCE_COUNT', payload: { dataSource: 'people', count: peopleCount.value } });
      if (contactsCount.status === 'fulfilled' && contactsCount.value !== null) dispatch({ type: 'SET_DATA_SOURCE_COUNT', payload: { dataSource: 'contacts', count: contactsCount.value } });
      countsLoaded.current = true;
    } catch (error) {
      console.error('❌ Failed to load background counts:', error);
    } finally {
      backgroundCountsLoading.current = false;
    }
  }, [state.initialized, loadDataSourceCount, dispatch, requestTrackerState.isSearching]);

  useEffect(() => () => { if (retryTimeoutRef.current) clearTimeout(retryTimeoutRef.current); }, []);
  useEffect(() => { if (apiKey && !state.initialized && !state.initializing) initializeApp(); }, [initializeApp, apiKey, state.initialized, state.initializing]);
  useEffect(() => { if (state.initialized && !countsLoaded.current && !backgroundCountsLoading.current) setTimeout(() => loadBackgroundCounts(), 100); }, [state.initialized, loadBackgroundCounts]);

  // Reload schema when data source changes
  useEffect(() => {
    let cancelled = false;
    if (state.schemaLoading && state.initialized) {
      const reloadSchema = async () => {
        try {
          const currentDataSource = state.searchState.dataSource;
          const schema = await getSchema(currentDataSource);
          if (cancelled) return;
          if (schema) {
            dispatch({ type: 'SET_SCHEMA', payload: schema, dataSource: currentDataSource });
            dispatch({ type: 'SET_SCHEMA_LOADING', payload: false });
          } else {
            dispatch({ type: 'SET_SCHEMA_ERROR', payload: 'Failed to load schema for data source' });
            dispatch({ type: 'SET_SCHEMA_LOADING', payload: false });
          }
        } catch (error) {
          if (cancelled) return;
          dispatch({ type: 'SET_SCHEMA_ERROR', payload: 'Failed to load schema for data source' });
          dispatch({ type: 'SET_SCHEMA_LOADING', payload: false });
        }
      };
      reloadSchema();
    }
    return () => { cancelled = true; };
  }, [state.schemaLoading, state.initialized, state.searchState.dataSource, getSchema, dispatch]);

  const performSearch = useCallback(async () => {
    if (!state.initialized || !state.schema || state.schemaLoading) return;
    const searchParamsString = JSON.stringify({ apiKey, searchState: state.searchState, navigationState: state.navigationState });
    if (searchParamsString === lastSearchParams.current) return;
    lastSearchParams.current = searchParamsString;
    if (isInitialLoad.current) dispatch({ type: 'SET_LOADING', payload: true });
    if (rateLimitInfo && rateLimitInfo.remaining < 2) {
      const waitTime = Math.max(0, rateLimitInfo.reset - Date.now());
      if (waitTime > 0) await new Promise(resolve => setTimeout(resolve, Math.min(waitTime, 5000)));
    }
    try {
      let result;
      
      // Use unified search API for all data sources including accounts
      const completeFilters = { ...state.searchState.filters };
      if (state.navigationState.view === 'list' && state.navigationState.listId) {
        completeFilters.list_ids = { type: 'picklist', values: [state.navigationState.listId], operator: 'OR' };
      }
      result = await searchContacts({ ...state.searchState, query: debouncedQuery, filters: completeFilters }, state.pageSize, state.schema);
      
      if (result) {
        const { response } = result;
        dispatch({ type: 'SET_SEARCH_RESULTS', payload: { contacts: response.hits, total: response.total, totalPages: response.total_pages, page: response.page, searchTime: response.query_time_ms, dataSource: state.searchState.dataSource, searchAccess: response.search_access } });
        if (isInitialLoad.current) { isInitialLoad.current = false; dispatch({ type: 'SET_LOADING', payload: false }); }
      }
    } catch (error) {
      if (error instanceof Error && 'code' in error && error.code === 'search_preview_limit') {
        dispatch({ type: 'SET_ERROR', payload: null });
        dispatch({ type: 'SET_PAGE', payload: 1 });
        if (isInitialLoad.current) { isInitialLoad.current = false; dispatch({ type: 'SET_LOADING', payload: false }); }
        return;
      }
      dispatch({ type: 'SET_ERROR', payload: error instanceof Error ? error.message : 'Search failed' });
      if (isInitialLoad.current) { isInitialLoad.current = false; dispatch({ type: 'SET_LOADING', payload: false }); }
    }
  }, [apiKey, debouncedQuery, state.searchState, state.navigationState, state.initialized, state.schema, state.schemaLoading, state.pageSize, searchContacts, searchAccounts, dispatch, rateLimitInfo]);

  useEffect(() => { if (state.initialized && state.schema) performSearch(); }, [state.initialized, state.schema, performSearch]);

  const refreshLists = useCallback(async () => {
    if (currentDataSource !== 'contacts') return;
    setListPickerLoading(true);
    try {
      const data = await getLists(false) as { success: boolean; lists?: UserList[] };
      setListPickerLists(data?.success ? data.lists || [] : []);
    } catch {
      setListPickerLists([]);
      showToast({ type: 'error', title: 'List Loading Failed', message: 'Failed to load contact lists' });
    } finally {
      setListPickerLoading(false);
    }
  }, [currentDataSource, getLists, showToast]);

  const refreshAccountLists = useCallback(async () => {
    if (currentDataSource !== 'accounts') return;
    setListPickerLoading(true);
    try {
      const data = await getAccountLists(true) as { success: boolean; lists?: AccountList[] };
      setAccountPickerLists(data?.success ? data.lists || [] : []);
    } catch {
      setAccountPickerLists([]);
      showToast({ type: 'error', title: 'List Loading Failed', message: 'Failed to load account lists' });
    } finally {
      setListPickerLoading(false);
    }
  }, [currentDataSource, getAccountLists, showToast]);

  useEffect(() => { if (currentDataSource === 'contacts') refreshLists(); }, [currentDataSource, refreshLists]);
  useEffect(() => { if (currentDataSource === 'accounts') refreshAccountLists(); }, [currentDataSource, refreshAccountLists]);
  useEffect(() => {
    const handleListsRefresh = () => { 
      if (currentDataSource === 'contacts') refreshLists(); 
      if (currentDataSource === 'accounts') refreshAccountLists();
    };
    window.addEventListener('lists-refresh', handleListsRefresh);
    return () => window.removeEventListener('lists-refresh', handleListsRefresh);
  }, [currentDataSource, refreshLists, refreshAccountLists]);

  const showSearchLoading = requestTrackerState.isSearching && !isInitialLoad.current;

  const renderView = useCallback(() => {
    switch (state.currentView) {
      case 'main':
      default:
        return (
          <>
            <ErrorBoundary><FilterSidebar isOpen={filterSidebarOpen} onToggle={() => setFilterSidebarOpen(!filterSidebarOpen)} /></ErrorBoundary>
            <div className={`main-content ${filterSidebarOpen ? 'filter-sidebar-open' : 'filter-sidebar-closed'}`} style={{ marginTop: 'var(--top-nav-height)' }}>
              {!isAuthenticated && apiKey === 'demo' && (() => {
                const pricePerContact = parseFloat(state.schema?.ui_config?.billing?.price_per_contact || '0.02');
                const creditsForTwenty = Math.floor(20 / pricePerContact);
                return (
                  <div className="demo-banner">
                    <div className="demo-banner-content">
                      <div className="demo-banner-text">
                        <strong>Pay-as-you-go pricing: ${formatPrice(pricePerContact)} per contact</strong>
                        <p>Sign in and add a payment method to get a $20 credit ({creditsForTwenty.toLocaleString()} contacts)</p>
                      </div>
                    </div>
                  </div>
                );
              })()}
              {apiKey && authUser && apiKey !== 'demo' && state.billingBalance?.has_payment_method === false && !authUser?.permissions?.includes('admin') && (() => {
                const globalPrice = parseFloat(state.schema?.ui_config?.billing?.price_per_contact || '0.02');
                const customPrice = state.billingBalance?.custom_price_per_contact ? parseFloat(String(state.billingBalance.custom_price_per_contact)) : null;
                const pricePerContact = customPrice || globalPrice;
                const creditsForTwenty = Math.floor(20 / pricePerContact);
                return (
                  <div className="payment-banner">
                    <div className="payment-banner-content">
                      <div className="payment-banner-text">
                        <strong>Pay-as-you-go pricing: ${formatPrice(pricePerContact)} per contact</strong>
                        <p>Add a payment method to get a $20 credit ({creditsForTwenty.toLocaleString()} contacts)</p>
                      </div>
                    </div>
                  </div>
                );
              })()}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', minHeight: 40 }}>
                <div className="page-breadcrumb" style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#6b7280', fontSize: 15 }}>
                  {currentDataSource === 'people' ? (
                    <><Search size={16} /><span>Search</span><ChevronRight size={14} /><Users size={16} /><span>People</span>
                      {showSearchLoading ? <span className="skeleton-text" /> : typeof state.totalResults === 'number' && <span className="breadcrumb-count-chip">{state.totalResults.toLocaleString()}</span>}
                    </>
                  ) : (currentDataSource as string) === 'company' ? (
                    <><Search size={16} /><span>Search</span><ChevronRight size={14} /><Building2 size={16} /><span>Company</span>
                      {showSearchLoading ? <span className="skeleton-text" /> : typeof state.totalResults === 'number' && <span className="breadcrumb-count-chip">{state.totalResults.toLocaleString()}</span>}
                    </>
                  ) : (currentDataSource as string) === 'accounts' ? (
                    <><List size={16} /><span>Lists</span><ChevronRight size={14} /><Building2 size={16} /><span>Accounts</span>
                      {showSearchLoading ? <span className="skeleton-text" /> : typeof state.totalResults === 'number' && <span className="breadcrumb-count-chip">{state.totalResults.toLocaleString()}</span>}
                    </>
                  ) : (
                    <><List size={16} /><span>Lists</span><ChevronRight size={14} /><BookOpen size={16} /><span>Contacts</span>
                      {showSearchLoading ? <span className="skeleton-text" /> : typeof state.totalResults === 'number' && <span className="breadcrumb-count-chip">{state.totalResults.toLocaleString()}</span>}
                    </>
                  )}
                </div>
                {(currentDataSource as string) === 'contacts' ? (
                  <div className="action-buttons">
                    {hasPermission('export') && <button className="btn btn-secondary" onClick={() => setExportFieldsModalOpen(true)} disabled={!allSelected && selectedCount === 0}>Export</button>}
                    {state.navigationState.listId && <button className="btn btn-secondary" onClick={() => { if (state.navigationState.listId) removeFromListJobAsync(state.navigationState.listId); }} disabled={!allSelected && selectedCount === 0} title="Remove selected contacts from this list (keeps contacts in your account)">Remove from List</button>}
                    {hasPermission('contacts') && <button className="btn btn-danger" onClick={() => { if (apiKey) deleteContactsJobAsync(apiKey); }} disabled={!allSelected && selectedCount === 0} title="Permanently delete selected contacts from your account (cannot be undone)">Delete</button>}
                    <div className="list-selector">
                      <label htmlFor="list-picker">List:</label>
                      <select id="list-picker" value={state.navigationState.listId || 'ALL'} onChange={e => setNavigation({ view: 'list', listId: e.target.value === 'ALL' ? null : e.target.value })} disabled={listPickerLoading}>
                        <option value="ALL">All Contacts</option>
                        {listPickerLists.map((list: UserList) => <option key={list.list_id} value={list.list_id}>{list.name}</option>)}
                      </select>
                    </div>
                    {state.navigationState.listId && hasPermission('contacts') && <button className="btn btn-ghost" onClick={() => { const list = listPickerLists.find(l => l.list_id === state.navigationState.listId); if (list && apiKey) deleteList(apiKey, list.list_id, list.name, () => { setNavigation({ view: 'list', listId: null }); window.dispatchEvent(new Event('lists-refresh')); }); }} disabled={listPickerLoading} title={`Delete list "${listPickerLists.find(l => l.list_id === state.navigationState.listId)?.name}"`}>Delete List</button>}
                  </div>
                ) : (currentDataSource as string) === 'accounts' ? (
                  <div className="action-buttons">
                    {hasPermission('export') && <button className="btn btn-secondary" onClick={() => setExportFieldsModalOpen(true)} disabled={!allSelected && selectedCount === 0}>Export</button>}
                    {state.navigationState.listId && <button className="btn btn-secondary" onClick={() => { if (state.navigationState.listId) removeFromAccountListJobAsync(state.navigationState.listId); }} disabled={!allSelected && selectedCount === 0} title="Remove selected accounts from this list (keeps accounts in your account)">Remove from List</button>}
                    {hasPermission('contacts') && <button className="btn btn-danger" onClick={() => { if (apiKey) deleteAccountsJobAsync(); }} disabled={!allSelected && selectedCount === 0} title="Permanently delete selected accounts from your account (cannot be undone)">Delete</button>}
                    <div className="list-selector">
                      <label htmlFor="list-picker">List:</label>
                      <select id="list-picker" value={state.navigationState.listId || 'ALL'} onChange={e => setNavigation({ view: 'list', listId: e.target.value === 'ALL' ? null : e.target.value })} disabled={listPickerLoading}>
                        <option value="ALL">All Accounts</option>
                        {accountPickerLists.map((list: AccountList) => <option key={list.list_id} value={list.list_id}>{list.name}</option>)}
                      </select>
                    </div>
                    {state.navigationState.listId && hasPermission('contacts') && <button className="btn btn-ghost" onClick={() => { const list = accountPickerLists.find(l => l.list_id === state.navigationState.listId); if (list && apiKey) deleteAccountList(apiKey, list.list_id, list.name, () => { setNavigation({ view: 'list', listId: null }); window.dispatchEvent(new Event('lists-refresh')); }); }} disabled={listPickerLoading} title={`Delete list "${accountPickerLists.find(l => l.list_id === state.navigationState.listId)?.name}"`}>Delete List</button>}
                  </div>
                ) : (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    {state.searchState.dataSource === 'company' ? (
                      <button className="button-primary" onClick={() => { if (hasPermission('contacts')) setListSelectionModalOpen(true); else showToast({ type: 'info', title: 'Sign In Required', message: 'Please sign in to add companies to lists. Click "Sign In" in the top right corner.', duration: 6000 }); }} disabled={selectedCount === 0 && !allSelected}>
                        {`Unlock ${allSelected ? state.totalResults.toLocaleString() : selectedCount} Compan${(allSelected ? state.totalResults : selectedCount) !== 1 ? 'ies' : 'y'}`}
                      </button>
                    ) : (
                      <button className="button-primary" onClick={() => { if (hasPermission('contacts')) setListSelectionModalOpen(true); else showToast({ type: 'info', title: 'Sign In Required', message: 'Please sign in to add contacts to lists. Click "Sign In" in the top right corner.', duration: 6000 }); }} disabled={selectedCount === 0 && !allSelected}>
                        {`Add ${allSelected ? state.totalResults.toLocaleString() : selectedCount} Contact${(allSelected ? state.totalResults : selectedCount) !== 1 ? 's' : ''}`}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <ErrorBoundary><DataTable loading={showSearchLoading} currentListId={state.navigationState.listId} initialLoadComplete={!isInitialLoad.current} /></ErrorBoundary>
              <ErrorBoundary><Pagination loading={showSearchLoading} /></ErrorBoundary>
            </div>
          </>
        );
      case 'terms': return <Terms onBack={() => setView('main')} />;
      case 'privacy': return <PrivacyPolicy onBack={() => setView('main')} />;
      case 'admin': return <AdminDashboard />; // ✅ RENDER NEW DASHBOARD
      case 'billing': return <AccountPage />;
      case 'enrichment': return <EnrichmentPage />;
    }
  }, [state.currentView, state.navigationState, state.totalResults, hasPermission, filterSidebarOpen, showSearchLoading, currentDataSource, allSelected, selectedCount, apiKey, exportSelectedContacts, removeFromListJobAsync, deleteContactsJobAsync, setNavigation, listPickerLists, listPickerLoading, deleteList, setListSelectionModalOpen, setView, isAuthenticated, authUser, state.billingBalance]);

  if (!state.initialized) return <LoadingState />;
  if (!state.apiConnected || state.schemaError) return <ErrorState error={state.error} schemaError={state.schemaError} apiKey={apiKey} rateLimitInfo={rateLimitInfo} circuitBreakerState={circuitBreakerState} />;
  const isPageView = ['terms', 'privacy', 'admin', 'billing', 'enrichment'].includes(state.currentView);

  return (
    <div className="app">
      <NotificationCenter />
      <ConfirmationDialog />
      <ErrorBoundary>
        <ListSelectionModal isOpen={listSelectionModalOpen} onClose={() => setListSelectionModalOpen(false)} selectAll={state.selection.selectAll} totalCount={state.totalResults} onConfirm={(listIds, selectAll, shouldRefreshLists, limit) => {
          if (apiKey && hasPermission('contacts')) {
            if (state.searchState.dataSource === 'people') unlockSelectedContacts(apiKey, listIds, selectAll, limit); 
            else if (state.searchState.dataSource === 'company') unlockSelectedAccounts(listIds, selectAll, limit);
            else addSelectedContactsToList(apiKey, listIds, selectAll, limit);
            if (shouldRefreshLists) window.dispatchEvent(new Event('lists-refresh'));
          } else showToast({ type: 'info', title: 'Sign In Required', message: 'Please sign in to add contacts to lists. Click "Sign In" in the top right corner.', duration: 6000 });
          setListSelectionModalOpen(false);
        }} selectedCount={selectedCount} />
        
        <ExportFieldsModal 
          isOpen={exportFieldsModalOpen} 
          onClose={() => setExportFieldsModalOpen(false)}
          onConfirm={(selectedFields, format) => {
            if (apiKey) {
              exportSelectedContacts(apiKey, format, selectedFields);
            }
          }}
          dataSource={state.searchState.dataSource}
        />
        
        <AccountListModal
          isOpen={accountListModalOpen}
          onClose={() => setAccountListModalOpen(false)}
          onSelectList={(listId) => {
            if (apiKey && hasPermission('contacts')) {
              addSelectedAccountsToList(listId);
              setAccountListModalOpen(false);
            } else {
              showToast({ type: 'info', title: 'Sign In Required', message: 'Please sign in to add companies to lists. Click "Sign In" in the top right corner.', duration: 6000 });
            }
          }}
          onCreateNew={() => {
            setAccountListModalOpen(false);
            setCreateAccountListModalOpen(true);
          }}
          selectedCount={selectedCount}
        />
        
        <CreateAccountListModal
          isOpen={createAccountListModalOpen}
          onClose={() => setCreateAccountListModalOpen(false)}
          onSuccess={(listId) => {
            if (apiKey && hasPermission('contacts')) {
              addSelectedAccountsToList(listId);
              setCreateAccountListModalOpen(false);
            }
          }}
        />
      </ErrorBoundary>
      <TopNav onFilterToggle={() => setFilterSidebarOpen(!filterSidebarOpen)} filterSidebarOpen={filterSidebarOpen} />
      <main className={`app-body ${isPageView ? 'page-view' : ''}`}>
        {renderView()}
      </main>
    </div>
  );
});
AppContent.displayName = 'AppContent';

const App: React.FC = () => {
  const { isAuthenticated, user, loading } = useFirebaseAuth();
  const [showLandingPage, setShowLandingPage] = useState(true);
  const [showLoginPage, setShowLoginPage] = useState(false);
  const [showTermsPage, setShowTermsPage] = useState(false);
  const [showPrivacyPage, setShowPrivacyPage] = useState(false);
  const [showDataDocsPage, setShowDataDocsPage] = useState(false);
  const apiKey = user?.apiKey || null;

  // Ensure clean default state for new visitors
  useEffect(() => {
    if (MAINTENANCE_MODE && !isAuthenticated) {
      setShowLandingPage(true);
      setShowLoginPage(false);
      return;
    }

    // If there's no specific flags set and user is not authenticated,
    // ensure we default to landing page
    const wantsLogin = localStorage.getItem('show_login_page') === 'true';
    const needsVerification = localStorage.getItem('needs_email_verification') === 'true';
    const useDemoMode = localStorage.getItem('use_demo_mode') === 'true';
    
    if (!isAuthenticated && !wantsLogin && !needsVerification && !useDemoMode) {

      setShowLandingPage(true);
      setShowLoginPage(false);
      setShowTermsPage(false);
      setShowPrivacyPage(false);
      setShowDataDocsPage(false);
    }
  }, []); // Run once on mount

  // Handle sign in from landing page
  const handleSignIn = () => {
    if (MAINTENANCE_MODE) return;
    setShowLandingPage(false);
    setShowLoginPage(true);
  };

  // Handle try for free from landing page (goes to sign up)
  const handleTryForFree = () => {
    if (MAINTENANCE_MODE) return;
    setShowLandingPage(false);
    localStorage.setItem('show_signup', 'true');
    setShowLoginPage(true);
  };

  // Handle terms navigation from landing page
  const handleTerms = () => {
    setShowLandingPage(false);
    setShowTermsPage(true);
  };

  // Handle privacy navigation from landing page
  const handlePrivacy = () => {
    setShowLandingPage(false);
    setShowPrivacyPage(true);
  };

  // Handle data docs navigation from landing page
  const handleDataDocs = () => {
    setShowLandingPage(false);
    setShowDataDocsPage(true);
  };

  useEffect(() => { 
    window.addEventListener('admin-login-success', () => { 
      setShowLandingPage(false); 
      setShowLoginPage(false); 
      localStorage.removeItem('show_login_page'); 
      localStorage.removeItem('needs_email_verification'); 
      setTimeout(() => setShowLoginPage(() => false), 50); 
    }); 
  }, []);

  useEffect(() => {
    const wantsLogin = localStorage.getItem('show_login_page') === 'true';
    const needsVerification = localStorage.getItem('needs_email_verification') === 'true';
    const useDemoMode = localStorage.getItem('use_demo_mode') === 'true';
    
    if (!loading && !isAuthenticated) {
      if (MAINTENANCE_MODE) {
        setShowLoginPage(false);
        localStorage.removeItem('show_login_page');
        localStorage.removeItem('show_signup');
        localStorage.removeItem('use_demo_mode');
        if (wantsLogin || needsVerification || useDemoMode) {
          setShowLandingPage(true);
          setShowTermsPage(false);
          setShowPrivacyPage(false);
          setShowDataDocsPage(false);
        } else if (!showTermsPage && !showPrivacyPage && !showDataDocsPage) {
          setShowLandingPage(true);
        }
      } else if (wantsLogin || needsVerification) {
        // User specifically requested login
        setShowLandingPage(false);
        setShowLoginPage(true);
        setShowTermsPage(false);
        setShowPrivacyPage(false);
        setShowDataDocsPage(false);
        localStorage.removeItem('show_login_page');
      } else if (useDemoMode) {
        // User chose "Try for Free" - go to demo mode
        setShowLandingPage(false);
        setShowLoginPage(false);
        setShowTermsPage(false);
        setShowPrivacyPage(false);
        setShowDataDocsPage(false);
      } else {
        // Default: show landing page for new visitors (this should be the most common case)
        setShowLandingPage(true);
        setShowLoginPage(false);
        setShowTermsPage(false);
        setShowPrivacyPage(false);
        setShowDataDocsPage(false);
      }
    } else if (isAuthenticated) {
      // User is authenticated - go to main app
      setShowLandingPage(false);
      setShowLoginPage(false);
      setShowTermsPage(false);
      setShowPrivacyPage(false);
      setShowDataDocsPage(false);
      localStorage.removeItem('show_login_page');
      localStorage.removeItem('use_demo_mode');
    }
  }, [loading, isAuthenticated]);

  if (loading) return <LoadingState />;
  
  // Show landing page for unauthenticated users (unless they've chosen demo or login)
  if (!isAuthenticated && showLandingPage) {
    return (
      <ErrorBoundary>
        <LandingPage 
          onSignIn={handleSignIn} 
          onTryForFree={handleTryForFree}
          onTerms={handleTerms}
          onPrivacy={handlePrivacy}
          onDataDocs={handleDataDocs}
        />
      </ErrorBoundary>
    );
  }

  // Show Terms page when requested from landing page
  if (showTermsPage) {
    return (
      <ErrorBoundary>
        <Terms onBack={() => {
          setShowTermsPage(false);
          setShowLandingPage(true);
        }} />
      </ErrorBoundary>
    );
  }

  // Show Privacy page when requested from landing page
  if (showPrivacyPage) {
    return (
      <ErrorBoundary>
        <PrivacyPolicy onBack={() => {
          setShowPrivacyPage(false);
          setShowLandingPage(true);
        }} />
      </ErrorBoundary>
    );
  }

  // Show Data Documentation page when requested from landing page
  if (showDataDocsPage) {
    return (
      <ErrorBoundary>
        <DataDocumentationPage onBack={() => {
          setShowDataDocsPage(false);
          setShowLandingPage(true);
        }} />
      </ErrorBoundary>
    );
  }
  
  // Show login page when requested
  if (!isAuthenticated && showLoginPage && !MAINTENANCE_MODE) {
    return (
      <ErrorBoundary>
        <AuthProvider initialApiKey={apiKey}>
          <AppProvider>
            <div className="app">
              <NotificationCenter />
              <LoginPage />
            </div>
          </AppProvider>
        </AuthProvider>
      </ErrorBoundary>
    );
  }
  
  // Show demo mode for users who clicked "Try for Free"
  if (!isAuthenticated && localStorage.getItem('use_demo_mode') === 'true' && !MAINTENANCE_MODE) {
    const demoApiKey = 'demo';
    const errorBoundaryResetKeys = [demoApiKey, 'demo-user'].filter(Boolean);
    return (
      <ErrorBoundary resetKeys={errorBoundaryResetKeys}>
        <AuthProvider initialApiKey={demoApiKey}>
          <AppProvider>
            <AppContent />
          </AppProvider>
        </AuthProvider>
      </ErrorBoundary>
    );
  }

  // Authenticated users
  const errorBoundaryResetKeys = [apiKey || '', user?.uid || ''].filter(Boolean);
  return (
    <ErrorBoundary resetKeys={errorBoundaryResetKeys}>
      <AuthProvider initialApiKey={apiKey}>
        <AppProvider>
          <AppContent />
        </AppProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
};

export default App;
